import { Loader2, Trophy } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetHrLeaderboardQuery } from '@/store/api/apiSlice';
import { cn } from '@/utils/cn';

function formatCurrency(amount: number | undefined) {
    if (amount === undefined || amount === null || isNaN(amount)) return '₦0';
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
    }).format(amount);
}

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
}

function rankMeta(rank: number) {
    if (rank === 1) return { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', emoji: '🥇' };
    if (rank === 2) return { color: 'bg-gray-100 text-gray-800 border-gray-200', emoji: '🥈' };
    if (rank === 3) return { color: 'bg-orange-100 text-orange-800 border-orange-200', emoji: '🥉' };
    return { color: 'bg-white text-gray-700 border-gray-200', emoji: '' };
}

export default function HrLeaderboard() {
    const currentUser = useAppSelector(selectCurrentUser);
    const { data: leaderboard = [], isLoading } = useGetHrLeaderboardQuery();

    const monthLabel = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'HR Partner'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'HR'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="h-9 w-9 rounded-lg bg-orange-50 flex items-center justify-center">
                                    <Trophy className="h-5 w-5 text-[#f37b23]" />
                                </div>
                                <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                                    HR Leaderboard
                                </p>
                            </div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                                Top performers this month
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                {monthLabel} · Ranked by active employers and commission earned.
                            </p>
                        </div>
                    </div>

                    {/* Leaderboard */}
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                            </div>
                        ) : leaderboard.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-16 px-6">
                                <div className="h-14 w-14 rounded-full bg-orange-50 flex items-center justify-center mb-4">
                                    <Trophy className="h-7 w-7 text-[#f37b23]" />
                                </div>
                                <h3 className="text-base font-semibold text-gray-900 mb-1">
                                    Leaderboard hasn't started yet
                                </h3>
                                <p className="text-sm text-gray-500 max-w-md">
                                    Once HR partners start referring this month, the top 10 will show up here. Get the
                                    first commission and claim the top spot.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[640px]">
                                    <thead className="bg-gray-50/60">
                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <th className="px-4 py-3 w-20">Rank</th>
                                            <th className="px-4 py-3">HR Partner</th>
                                            <th className="px-4 py-3">Active employers</th>
                                            <th className="px-4 py-3">Commission ({monthLabel})</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaderboard.map((entry) => {
                                            const isMe = entry.hrId === currentUser?.id;
                                            const meta = rankMeta(entry.rank);
                                            return (
                                                <tr
                                                    key={entry.hrId}
                                                    className={cn(
                                                        'border-b border-gray-100 transition-colors',
                                                        isMe ? 'bg-orange-50/60' : 'hover:bg-gray-50/60',
                                                    )}
                                                >
                                                    <td className="px-4 py-4">
                                                        <div
                                                            className={cn(
                                                                'inline-flex items-center justify-center gap-1 px-3 py-1 rounded-lg text-sm font-semibold border',
                                                                meta.color,
                                                            )}
                                                        >
                                                            {meta.emoji && <span>{meta.emoji}</span>}
                                                            <span>#{entry.rank}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="h-9 w-9 rounded-full bg-gray-900 text-white text-xs font-semibold flex items-center justify-center">
                                                                {getInitials(
                                                                    entry.hrName.split(' ')[0],
                                                                    entry.hrName.split(' ').slice(1).join(' '),
                                                                )}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">
                                                                    {entry.hrName}
                                                                    {isMe && (
                                                                        <span className="ml-2 text-[11px] font-semibold text-[#f37b23] uppercase tracking-wider">
                                                                            You
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-gray-800">
                                                        {entry.activeEmployers}
                                                    </td>
                                                    <td className="px-4 py-4 text-sm font-semibold text-gray-900">
                                                        {formatCurrency(entry.monthCommission)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
